import { z } from "zod";
import { toFirestoreSchema } from "../utils/toFirestoreSchema";
import { BioContentSchema } from "./bio.schema";
import { GenderEnumSchema } from "./gender.schema";

/**
 * Gender Enum options for user profile — owned by @fitties/shared.
 */
export { type Gender, GenderEnumSchema } from "./gender.schema";

/**
 * Legacy Desired Gender Enum options for user matching preferences
 */
export const LegacyDesiredGenderEnumSchema = z.union([GenderEnumSchema, z.literal("anyone")]);

export const NotificationPreferencesSchema = z
  .object({
    sms_enabled: z.boolean().default(true),
    sms_like: z.boolean().default(true),
    sms_match: z.boolean().default(true),
    sms_message: z.boolean().default(true),
    sms_unlock_reminder: z.boolean().default(true),
  })
  .default({
    sms_enabled: true,
    sms_like: true,
    sms_match: true,
    sms_message: true,
    sms_unlock_reminder: true,
  });

export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;
export type NotificationPreferencesInput = z.input<typeof NotificationPreferencesSchema>;

/**
 * ONBOARDING USER SCHEMA
 * User has registered but hasn't completed profile setup
 * All profile fields are optional (they may be working through the wizard)
 */
export const OnboardingUserSchema = z.object({
  /** Unique user identifier (set by Auth Service) */
  uid: z.string().describe("Unique user identifier"),

  /** User's phone number in E.164 format (primary identifier for phone auth) */
  phone_number: z.string().describe("Phone number in E.164 format"),

  /**
   * @deprecated Email is optional for phone auth users. May be added later for notifications.
   */
  email: z.email().optional().describe("Optional email address"),

  /** Account creation timestamp (set by Auth Service) */
  created_time: z.date().describe("When the account was created"),

  /** Display name shown to other users (from name step) */
  display_name: z.string().optional().describe("Public display name"),

  /** Flattened biography text (from bio step). Q/A uses the answer. */
  bio: z.string().max(500).optional().describe("User's profile bio"),

  /** Structured bio for message-chain display */
  bioContent: BioContentSchema.optional().describe("Structured bio content"),

  /** User's date of birth (from birthday step) */
  date_of_birth: z.date().optional().describe("User's date of birth"),

  /** User's gender identity (from gender step) */
  gender: GenderEnumSchema.optional().describe("User's gender identity"),

  /** What gender(s) the user wants to match with (from preference step) */
  desired_genders: z.array(GenderEnumSchema).optional().describe("Preferred gender(s) for matching"),

  /** Profile photo URL (from photo step) */
  photo_url: z.string().url().optional().describe("URL to user's profile photo"),
});

/**
 * Onboarding field names that define the step flow
 * These must match the schema field names exactly
 */
export const OnboardingFieldSchema = OnboardingUserSchema.pick({
  display_name: true,
  bio: true,
  date_of_birth: true,
  gender: true,
  desired_genders: true,
  photo_url: true,
});

const OnboardingFieldsEnumSchema = OnboardingFieldSchema.keyof();
export const ONBOARDING_FIELDS = OnboardingFieldsEnumSchema.options;

export type OnboardingField = z.infer<typeof OnboardingFieldsEnumSchema>;

/**
 * USER SCHEMA (Complete)
 * User has finished profile setup and can use the full app
 * Extends OnboardingUser but makes profile fields required
 */
export const UserSchema = OnboardingUserSchema.required({
  display_name: true,
  bio: true,
  date_of_birth: true,
  gender: true,
  desired_genders: true,
  photo_url: true,
}).extend({
  /** Geographic location for matching */
  location: z.string().optional().describe("User's location for proximity matching"),

  /** Array of user IDs this user has liked (one-sided or mutual) */
  liked_uids: z.array(z.string()).default([]).describe("User IDs this user has liked"),

  /** Array of user IDs of confirmed mutual matches with active chats */
  chat_partner_uids: z.array(z.string()).default([]).describe("User IDs of confirmed mutual matches with active chats"),

  /** Array of user IDs that this user has rejected */
  rejected_uids: z.array(z.string()).default([]).describe("Rejected user IDs"),

  /** Array of user IDs that this user has blocked */
  blockedUsers: z.array(z.string()).default([]).describe("Blocked user IDs"),

  /** Last successful payment timestamp (e.g. event access unlock) */
  purchaseTime: z.date().optional().describe("When the last purchase completed"),

  /** Most recent event ID from QR entry */
  lastKnownEventId: z.string().optional().describe("Most recent event ID"),

  /** Most recent event entry timestamp */
  lastKnownEventEnteredAt: z.date().optional().describe("Most recent event entry time"),

  /** Legacy optional field; no longer written or used for lockout */
  activeEventAccessUntil: z.date().optional().describe("Legacy event access expiry timestamp (unused)"),

  /** Event IDs unlocked permanently by payment */
  unlockedEventIds: z.array(z.string()).optional().describe("Paid unlocked event IDs"),

  /** Event IDs the user has entered via QR (cumulative, including after switching events) */
  enteredEventIds: z.array(z.string()).optional().describe("All event IDs entered via QR"),

  /** VIP profile boost — discovery lists boosted profiles first for other users this event */
  isBoosted: z.boolean().optional().describe("Profile boost active for current event context"),

  /**
   * When true (default), profile stays in discovery after the 24h "Here tonight" window
   * ("Here this week"). When false, profile is hidden from discovery outside that window.
   */
  includeInWeeklyPool: z.boolean().default(true).describe("Appear in discovery as Here this week"),

  /**
   * When true, only appear in discovery to users this person has already liked
   * (opt-in; missing/undefined means off)
   */
  hide_unless_liked: z
    .boolean()
    .optional()
    .describe("When true, only appear in discovery to users this person has already liked"),

  /** Set by requestAccountDeletion; clients cannot write this field */
  deletionRequestedAt: z.date().optional().describe("When the user requested account deletion"),

  /** Per-user SMS notification preferences */
  notification_preferences: NotificationPreferencesSchema.describe("SMS notification preferences"),
});

/**
 * Firestore Admin / emulator user document schema.
 * Top-level `z.date()` fields become Timestamp-like (`toDate()`).
 * App code keeps using {@link UserSchema} after Timestamp → Date conversion.
 */
export const UserRecordSchema = toFirestoreSchema(UserSchema);

export type OnboardingUserInput = z.input<typeof OnboardingUserSchema>;
export type OnboardingUser = z.infer<typeof OnboardingUserSchema>;
export type UserInput = z.input<typeof UserSchema>;
export type User = z.infer<typeof UserSchema>;
export type UserRecord = z.infer<typeof UserRecordSchema>;
export type UserRecordInput = z.input<typeof UserRecordSchema>;
